import { useFeatureFlag, isString } from "@sinch/feature-flags";
import {
  createContext,
  useMemo,
  useCallback,
  useContext,
  type FC,
  type PropsWithChildren,
} from "react";
import { useIntl } from "react-intl";
import type { ShellUser } from "../../../api/types";
import {
  localeMoney,
  localeNumber,
} from "../../../components/NavigationBar/utils/helpers";
import type { TranslationKeys } from "../../../intl/intl.types";
import { useMonthlyUsage, useAccountBalance } from "../hooks";

const SUPPORT_FEATURE = "support";
const BILLING_BALANCE_MONEY = "billing.balance.money";
const BILLING_BALANCE_CREDIT = "billing.balance.credit";
const BILLING_BALANCE_USAGE = "billing.balance.usage";

export type ProfileContextValue = {
  name: string;
  usageInfo: string;
  isSupport: boolean;
  links: {
    supportIndex: string;
    helpCenter: string;
  };
};

const ProfileContext = createContext<ProfileContextValue | undefined>(
  undefined
);

export type ProfileContext = {
  userAccount: ShellUser | undefined;
};
export const ProfileContextProvider: FC<PropsWithChildren<ProfileContext>> = ({
  children,
  userAccount,
}) => {
  const intl = useIntl();
  const monthlyUsage = useMonthlyUsage(userAccount);

  const hasFeature = useCallback(
    (feature: string): boolean => !!userAccount?.rights.includes(feature),
    [userAccount]
  );

  const shouldFetchAccountBalance = useMemo(
    () =>
      hasFeature(BILLING_BALANCE_MONEY) || hasFeature(BILLING_BALANCE_CREDIT),
    [hasFeature]
  );

  const accountBalance = useAccountBalance(shouldFetchAccountBalance);
  const mmSupportLink = useFeatureFlag(
    "mmSupportLink",
    isString,
    "https://support.messagemedia.com/hc/en-us"
  );

  const isSupport = !!userAccount?.rights.includes(SUPPORT_FEATURE);

  const links = {
    supportIndex: "/support/v2",
    helpCenter: mmSupportLink,
  };

  const getPaymentTypeLabelKey = useCallback((): TranslationKeys => {
    if (hasFeature(BILLING_BALANCE_CREDIT)) {
      return "menuItemProfileUsageCredit";
    }
    if (hasFeature(BILLING_BALANCE_MONEY)) {
      return "menuItemProfileUsageMoney";
    }
    return "menuItemProfileMonthlyUsage";
  }, [hasFeature]);

  const usageInfo = useMemo(() => {
    let value = "-";

    if (
      hasFeature(BILLING_BALANCE_MONEY) &&
      accountBalance?.remainingMoney?.MT_SMS !== undefined
    ) {
      value = localeMoney(accountBalance?.remainingMoney?.MT_SMS);
    }
    if (
      hasFeature(BILLING_BALANCE_CREDIT) &&
      accountBalance?.remainingCredits?.MT_SMS !== undefined
    ) {
      value = localeNumber(accountBalance?.remainingCredits?.MT_SMS);
    }
    if (hasFeature(BILLING_BALANCE_USAGE) && monthlyUsage !== undefined) {
      value = localeNumber(monthlyUsage);
    }

    return intl.formatMessage({ id: getPaymentTypeLabelKey() }, { value });
  }, [accountBalance, monthlyUsage, getPaymentTypeLabelKey, intl, hasFeature]);

  const value = {
    name: userAccount?.name || "",
    usageInfo,
    isSupport,
    links,
  };

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};
export const useProfileContext = (): ProfileContextValue => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error(
      "useProfileContext must be used within a ProfileContextProvider"
    );
  }
  return context;
};
